/**
 * E6 -- the bank answers a supervisor's question.
 *
 * The examiner lodges it from his workspace; it is answered here,
 * because this is where the answers already are. The reply is the P177
 * bundle over whichever assurance questions address it, plus a note in
 * the bank's own words -- an examiner may ask something none of the
 * templated questions covers, and a written reply is a better answer
 * than a pack that does not address the question.
 *
 * Oldest first, unlike everything else on this surface: an unanswered
 * supervisory question gets more serious with age.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { formatDateTime } from "shared/time/formatDate";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, type ApiError } from "shared/api/client";
import { useToast } from "shared/toast/ToastProvider";
import { CitePicker, type Citation } from "shared/assurance/CitePicker";
import { currentAuthToken } from "shared/api/client";

import { Bdi } from "shared/components/Bdi";
interface RfiCitation {
  kind: string;
  ref: string;
  label: string;
  chain: string | null;
  detail: Record<string, unknown>;
}

interface Rfi {
  id: string;
  examiner_username: string;
  question: string;
  status: string;
  asked_at: string;
  answered_at: string | null;
  answer_note: string | null;
  answered_by: string | null;
  citations: RfiCitation[];
  has_pack: boolean;
}

/**
 * The pack the bank sent, from the bank's own side.
 *
 * Same posture as `useDownloadAnswerPack` (manual bearer, bypassing
 * the JSON parse) because the body is a zip. Served from the stored
 * bytes, not recomposed: an assurance answer is built from live state,
 * so rebuilding it later would hand the reader a different estate
 * under the same question.
 */
async function downloadPack(id: string): Promise<void> {
  const token = currentAuthToken();
  const res = await fetch(`/v1/assurance/rfis/${encodeURIComponent(id)}/pack.zip`, {
    headers: {
      Accept: "application/zip",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  if (!res.ok) return;
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement("a");
  a.href = url;
  a.download = `marsad-rfi-${id}.zip`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1_000);
}

export function RfiInbox({
  questionIds,
}: {
  /** The assurance questions available to answer with. */
  questionIds: { id: string; text: string }[];
}) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const [openId, setOpenId] = useState<string | null>(null);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [note, setNote] = useState("");
  // D3 -- the review step before an answer leaves the bank.
  const [confirming, setConfirming] = useState(false);
  const [cited, setCited] = useState<Citation[]>([]);

  // B3 -- both, so the surface that answered can say what it answered.
  // It fetched open_only and dropped each row on send, which left the
  // bank unable to read back what it had told a regulator while the
  // regulator's own workspace kept the whole correspondence.
  const rfis = useQuery<Rfi[]>({
    queryKey: ["assurance", "rfis"],
    queryFn: () => api.get<Rfi[]>("/v1/assurance/rfis?open_only=false"),
  });

  const send = useMutation<Rfi, ApiError, { id: string }>({
    mutationFn: ({ id }) =>
      api.post<Rfi>(`/v1/assurance/rfis/${id}/answer`, {
        question_ids: [...picked],
        ...(note.trim() ? { note: note.trim() } : {}),
        citations: cited.map((c) => ({ kind: c.kind, ref: c.ref })),
      }),
    onSuccess: () => {
      // C5 -- the toast promised "the evidence behind it" whatever was
      // sent. An answer with no question selected carries no citations,
      // so its pack's proof.json is empty: signed, and proving nothing.
      // Saying otherwise is the product making a claim it cannot back,
      // to the one reader whose job is checking claims.
      const carriesEvidence = picked.size > 0 || cited.length > 0;
      setOpenId(null);
      setPicked(new Set());
      setNote("");
      setCited([]);
      void qc.invalidateQueries({ queryKey: ["assurance", "rfis"] });
      toast.push({
        tone: "success",
        message: carriesEvidence
          ? t("assurance.rfi.sent")
          : t("assurance.rfi.sentNoteOnly"),
      });
    },
    onError: (e) => toast.push({ tone: "error", message: e.detail }),
  });

  const all = rfis.data ?? [];
  const rows = all.filter((r) => r.status === "open");
  // Newest answer first: the open list ages upward because a waiting
  // question gets more serious, but a record is read most-recent-first.
  const answered = all
    .filter((r) => r.status !== "open")
    .slice()
    .reverse();
  // Nothing waiting is worth saying: silence reads as a surface that
  // does not work.
  if (rows.length === 0 && answered.length === 0) return null;

  return (
    <div className="space-y-3">
      {rows.length > 0 && (
        <section
          data-testid="assurance-rfi-inbox"
          className="rounded border border-amber-200 bg-amber-50/60 p-3"
        >
          <h2 className="font-mono text-xs uppercase tracking-wide text-amber-900">
            {t("assurance.rfi.inboxHeading", { count: rows.length })}
          </h2>
          <ul className="mt-2 space-y-2">
            {rows.map((r) => (
              <li
                key={r.id}
                data-testid={`assurance-rfi-${r.id}`}
                className="rounded border border-paper-2 bg-white px-3 py-2 text-sm"
              >
                <p className="text-slate-800">{r.question}</p>
                <p className="text-xs text-slate-500">
                  {t("assurance.rfi.askedBy", { who: r.examiner_username })}
                </p>
                {openId === r.id ? (
                  <div className="mt-2 space-y-2">
                    <div className="flex flex-wrap gap-2">
                      {questionIds.map((q) => (
                        <label
                          key={q.id}
                          className="flex items-center gap-1.5 text-xs text-slate-700"
                        >
                          <input
                            type="checkbox"
                            data-testid={`assurance-rfi-pick-${q.id}`}
                            checked={picked.has(q.id)}
                            onChange={() =>
                              setPicked((prev) => {
                                const next = new Set(prev);
                                if (next.has(q.id)) next.delete(q.id);
                                else next.add(q.id);
                                return next;
                              })
                            }
                          />
                          {q.text}
                        </label>
                      ))}
                    </div>
                    <textarea
                      dir="auto"
                      data-testid="assurance-rfi-note"
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      rows={2}
                      placeholder={t("assurance.rfi.notePlaceholder")}
                      className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
                    />
                    <CitePicker chosen={cited} onChange={setCited} />
                    {/* D3 -- what is about to leave the bank, before it
                    does. This sent on one click, on the one action in
                    the product that puts an answer in front of a
                    regulator: no restatement of what was chosen, and no
                    moment to notice a citation picked by mistake.
                    Two steps, and the first one shows the answer. */}
                    {confirming ? (
                      <div
                        data-testid="assurance-rfi-confirm"
                        className="space-y-2 rounded border border-brand-brass/40 bg-brand-brass/5 p-2"
                      >
                        <p className="text-xs font-medium text-slate-800">
                          {t("assurance.rfi.confirmHeading")}
                        </p>
                        <p className="text-xs text-slate-700">
                          {t("assurance.rfi.confirmCounts", {
                            questions: picked.size,
                            citations: cited.length,
                          })}
                        </p>
                        {/* D3 (round 8) -- and WHICH citations, by name.
                        The panel counted them. A count is exactly the
                        thing that cannot catch the mistake this step
                        exists to catch: citing the wrong control. */}
                        {cited.length > 0 && (
                          <ul
                            data-testid="assurance-rfi-confirm-citations"
                            className="space-y-0.5 text-xs text-slate-600"
                          >
                            {cited.map((c) => (
                              <li key={`${c.kind}:${c.ref}`} dir="auto">
                                <Bdi>{c.label}</Bdi>
                              </li>
                            ))}
                          </ul>
                        )}
                        {note.trim() && (
                          <p className="text-xs text-slate-600" dir="auto">
                            <Bdi>{note.trim()}</Bdi>
                          </p>
                        )}
                        <div className="flex gap-2">
                          <button
                            type="button"
                            data-testid="assurance-rfi-send"
                            disabled={send.isPending}
                            onClick={() => send.mutate({ id: r.id })}
                            className="rounded bg-brand-brass px-3 py-1 text-sm font-medium text-brand-navy hover:bg-brand-brassDark disabled:opacity-50"
                          >
                            {send.isPending
                              ? t("assurance.rfi.sendingAnswer")
                              : t("assurance.rfi.confirmSend")}
                          </button>
                          <button
                            type="button"
                            data-testid="assurance-rfi-confirm-back"
                            onClick={() => setConfirming(false)}
                            className="rounded border border-slate-300 px-3 py-1 text-sm text-slate-700"
                          >
                            {t("assurance.rfi.confirmBack")}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        type="button"
                        data-testid="assurance-rfi-review"
                        disabled={
                          picked.size === 0 && cited.length === 0 && !note.trim()
                        }
                        onClick={() => setConfirming(true)}
                        className="rounded bg-brand-brass px-3 py-1 text-sm font-medium text-brand-navy hover:bg-brand-brassDark disabled:opacity-50"
                      >
                        {t("assurance.rfi.reviewAnswer")}
                      </button>
                    )}
                  </div>
                ) : (
                  <button
                    type="button"
                    data-testid={`assurance-rfi-open-${r.id}`}
                    onClick={() => setOpenId(r.id)}
                    className="mt-1 text-xs font-medium text-brand-brassDark hover:underline"
                  >
                    {t("assurance.rfi.answer")}
                  </button>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {answered.length > 0 && (
        <section
          data-testid="assurance-rfi-answered"
          className="rounded border border-paper-2 bg-white p-3"
        >
          <h2 className="font-mono text-xs uppercase tracking-wide text-ink-muted">
            {t("assurance.rfi.answeredHeading", { count: answered.length })}
          </h2>
          <ul className="mt-2 space-y-2">
            {answered.map((r) => (
              <li
                key={r.id}
                data-testid={`assurance-rfi-answered-${r.id}`}
                className="rounded border border-paper-2 px-3 py-2 text-sm"
              >
                <p className="text-slate-800">
                  <Bdi>{r.question}</Bdi>
                </p>
                <p className="mt-0.5 text-xs text-slate-500">
                  {t("assurance.rfi.answeredBy", {
                    who: r.answered_by ?? t("assurance.rfi.answererUnknown"),
                    /* Round 11 F4 -- `toLocaleString()` with no locale
                       follows the BROWSER, so the bank's own answered
                       list read "9/16/2026, 2:26:01 PM" beside the
                       examiner's copy of the same event reading
                       "16 Sept 2026, 14:26". F3 put one place in
                       charge of how a date looks; this call predates
                       it and never went through. */
                    at: formatDateTime(r.answered_at, i18n.language),
                  })}
                </p>
                {r.answer_note ? (
                  <p
                    data-testid={`assurance-rfi-answered-note-${r.id}`}
                    className="mt-1 border-s-2 border-paper-2 ps-2 text-[13px] text-slate-700"
                  >
                    <Bdi>{r.answer_note}</Bdi>
                  </p>
                ) : null}
                {/* What the bank pointed at. An answer whose citations
                  can only be read by unzipping the archive is a record
                  the bank cannot check against the question. */}
                {Array.isArray(r.citations) && r.citations.length > 0 ? (
                  <ul className="mt-1 space-y-0.5">
                    {r.citations.map((c) => (
                      <li
                        key={`${c.kind}:${c.ref}`}
                        data-testid="assurance-rfi-answered-citation"
                        className="font-mono text-xs text-slate-600"
                        dir="auto"
                      >
                        {c.label}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p
                    data-testid={`assurance-rfi-answered-nocite-${r.id}`}
                    className="mt-1 text-xs text-slate-500"
                  >
                    {/* Stated rather than left blank: an answer that
                      cited nothing is a fact about the answer, and the
                      reader should not have to infer it from silence. */}
                    {t("assurance.rfi.answeredNoCitations")}
                  </p>
                )}
                {r.has_pack ? (
                  <button
                    type="button"
                    data-testid={`assurance-rfi-answered-pack-${r.id}`}
                    onClick={() => void downloadPack(r.id)}
                    className="mt-1 text-xs font-medium text-brand-brassDark hover:underline"
                  >
                    {t("assurance.rfi.answeredPack")}
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
